/**
 * The live preview (section 7): one screen of the app as a TV (16:9) or a phone, drawn from what
 * layout.ts works out, in the brand's dark ground, accent and fonts, with real artwork. A sketch of
 * the apps' layout, not the native apps (the line under it says so). The apps are dark whatever the
 * console's theme, so the sketch uses the kit's fixed shades (the themed text tokens resolve at the
 * page root and would follow the console's light theme). Clicking a row selects it in
 * the list beside it, and the selected row is outlined here.
 */

import type { PreviewBadge, PreviewCard, PreviewRow, PreviewScreen } from "./layout";

export type Device = "tv" | "phone";

interface Props {
  screen: PreviewScreen;
  device: Device;
  brandName: string;
  menu: Array<{ id: string; label: string }>;
  currentId: string;
  fonts: { display: string; body: string };
  selected: number | null;
  onSelect: (index: number) => void;
}

/** Card widths as a share of the row, per device (cards bleed off the edge, as rows do on a TV). */
const WIDTH: Record<Device, Record<PreviewRow["shape"], string>> = {
  tv: { landscape: "w-[22%]", poster: "w-[11.5%]", numbered: "w-[11.5%]", editorial: "w-[38%]", square: "w-[13%]", collection: "w-[30%]" },
  phone: { landscape: "w-[58%]", poster: "w-[31%]", numbered: "w-[31%]", editorial: "w-[86%]", square: "w-[34%]", collection: "w-[72%]" },
};
const ASPECT: Record<PreviewRow["shape"], string> = { landscape: "aspect-video", poster: "aspect-[2/3]", numbered: "aspect-[2/3]", editorial: "aspect-video", square: "aspect-square", collection: "aspect-video" };

function Badge({ badge }: { badge: PreviewBadge }) {
  const tone = badge.tone === "live" ? "bg-critical text-on-critical" : badge.tone === "accent" ? "bg-accent text-on-accent" : "bg-shade-04 text-shade-09";
  return <span className={`rounded-[0.3em] px-[0.45em] py-[0.1em] text-[0.62em] font-semibold uppercase tracking-wide ${tone}`}>{badge.label}</span>;
}

function Card({ card, shape, device, display }: { card: PreviewCard; shape: PreviewRow["shape"]; device: Device; display: PreviewRow["display"] }) {
  const overlay = shape === "editorial" || shape === "collection";
  return (
    <div className={`${display === "grid" ? "" : WIDTH[device][shape]} shrink-0`}>
      <div className="relative flex items-end gap-[0.3em]">
        {shape === "numbered" && card.rank ? <span className="-me-[0.4em] text-[3.2em] font-bold leading-none text-shade-08/75" style={{ fontFamily: "inherit" }} aria-hidden="true">{card.rank}</span> : null}
        <div className={`relative w-full overflow-hidden rounded-[0.5em] bg-shade-04 ${ASPECT[shape]}`} style={{ backgroundColor: card.color }}>
          {card.image ? <img src={card.image} alt="" className="absolute inset-0 h-full w-full object-cover" loading="lazy" /> : <span className="absolute inset-0 flex items-center justify-center p-[0.5em] text-center text-[0.8em] text-shade-08/75">{card.title}</span>}
          {card.badges.length ? <span className="absolute start-[0.4em] top-[0.4em] flex gap-[0.25em]">{card.badges.map((b) => <Badge key={b.label} badge={b} />)}</span> : null}
          {overlay ? (
            <span className="absolute inset-x-0 bottom-0 flex flex-col bg-linear-to-t from-shade-02 to-transparent p-[0.6em] pt-[2em]">
              <span className="truncate text-[1.1em] font-semibold text-shade-09">{card.title}</span>
              {card.meta ? <span className="truncate text-[0.8em] text-shade-08/75">{card.meta}</span> : null}
            </span>
          ) : null}
          {typeof card.progress === "number" ? <span className="absolute inset-x-[0.4em] bottom-[0.4em] h-[0.25em] overflow-hidden rounded-full bg-shade-06"><span className="block h-full bg-accent" style={{ width: `${card.progress * 100}%` }} /></span> : null}
        </div>
      </div>
      {!overlay ? (
        <div className="mt-[0.35em] flex flex-col">
          <span className="truncate text-[0.85em] text-shade-09">{card.title}</span>
          {card.meta ? <span className="truncate text-[0.7em] text-shade-08/75">{card.meta}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

function Row({ row, device, selected, onSelect, fonts }: { row: PreviewRow; device: Device; selected: boolean; onSelect: () => void; fonts: Props["fonts"] }) {
  return (
    <button type="button" onClick={onSelect} aria-pressed={selected} aria-label={`Row: ${row.title}. Select it in the list`}
      className={`block w-full rounded-[0.6em] px-[0.6em] py-[0.5em] text-start outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-accent ${selected ? "ring-2 ring-accent" : "hover:ring-1 hover:ring-shade-06"}`}>
      <div className="mb-[0.5em] flex items-baseline gap-[0.6em]">
        <span className="truncate text-[1.15em] font-semibold text-shade-09" style={{ fontFamily: fonts.display }}>{row.title}</span>
        {row.sample ? <span className="truncate text-[0.7em] text-shade-08/75">{row.sample}</span> : null}
      </div>
      {row.empty ? (
        <div className="rounded-[0.5em] border border-dashed border-shade-05 p-[0.8em] text-[0.8em] text-shade-08/75">{row.empty}</div>
      ) : row.display === "schedule" ? (
        <div className="flex flex-col gap-[0.3em]">
          {row.schedule!.map((s) => (
            <div key={s.stage} className="flex items-center gap-[0.4em]">
              <span className="w-[16%] shrink-0 truncate text-[0.75em] text-shade-08/75">{s.stage}</span>
              <div className="flex grow gap-[0.3em] overflow-hidden">{s.blocks.slice(0, 5).map((b) => <span key={b.title + b.start} className="min-w-0 flex-1 truncate rounded-[0.3em] bg-shade-04 px-[0.4em] py-[0.3em] text-[0.75em] text-shade-09">{b.title}</span>)}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={display(row, device)}>
          {row.cards.slice(0, row.display === "grid" ? (device === "tv" ? 16 : 6) : 12).map((c) => <Card key={c.id} card={c} shape={row.shape} device={device} display={row.display} />)}
        </div>
      )}
    </button>
  );
}

const display = (row: PreviewRow, device: Device) =>
  row.display === "grid" ? `grid gap-[0.6em] ${device === "tv" ? "grid-cols-8" : "grid-cols-3"}` : "flex gap-[0.6em] overflow-hidden";

function Hero({ screen, device, fonts, selected, onSelect }: { screen: PreviewScreen; device: Device; fonts: Props["fonts"]; selected: boolean; onSelect: (i: number) => void }) {
  const hero = screen.hero!;
  const tv = device === "tv";
  return (
    <button type="button" onClick={() => onSelect(hero.fromRow)} aria-label={`The big picture at the top: ${hero.title}. Select its row`}
      className={`relative block w-full overflow-hidden text-start outline-none ${tv ? "aspect-[16/6]" : "aspect-[4/5]"} ${selected ? "ring-2 ring-inset ring-accent" : ""}`} style={{ backgroundColor: hero.color }}>
      {hero.image ? <img src={hero.image} alt="" className="absolute inset-0 h-full w-full object-cover" /> : null}
      <span className={`absolute inset-0 ${tv ? "bg-linear-to-r from-shade-02 via-shade-02/60 to-transparent" : "bg-linear-to-t from-shade-02 via-shade-02/50 to-transparent"}`} />
      <span className="absolute inset-x-0 bottom-0 h-1/3 bg-linear-to-t from-shade-02 to-transparent" />
      <span className={`absolute flex flex-col gap-[0.5em] ${tv ? "start-[4%] bottom-[12%] w-[46%]" : "inset-x-[6%] bottom-[6%]"}`}>
        {hero.logo ? <img src={hero.logo} alt={hero.title} className="max-h-[4em] max-w-[70%] object-contain object-left" /> : <span className={`${tv ? "text-[2.6em]" : "text-[2em]"} font-bold leading-tight text-shade-09`} style={{ fontFamily: fonts.display }}>{hero.title}</span>}
        <span className="flex flex-wrap items-center gap-[0.4em] text-[0.8em] text-shade-08/75">{hero.badges.map((b) => <Badge key={b.label} badge={b} />)}{hero.meta ? <span>{hero.meta}</span> : null}</span>
        {hero.synopsis ? <span className="line-clamp-2 text-[0.85em] text-shade-09">{hero.synopsis}</span> : null}
        <span className="flex gap-[0.5em]">
          <span className="rounded-full bg-accent px-[1.2em] py-[0.45em] text-[0.85em] font-semibold text-on-accent">▶ Play</span>
          <span className="rounded-full bg-shade-04 px-[1.2em] py-[0.45em] text-[0.85em] font-semibold text-shade-09">More info</span>
        </span>
      </span>
    </button>
  );
}

export function AppPreview({ screen, device, brandName, menu, currentId, fonts, selected, onSelect }: Props) {
  const tv = device === "tv";
  const nav = (
    <div className={`flex items-center gap-[1.2em] ${tv ? "px-[4%] py-[1em]" : "justify-around border-t border-shade-04 px-[0.6em] py-[0.7em]"}`}>
      {tv ? <span className="me-[1em] text-[1.2em] font-bold text-shade-09" style={{ fontFamily: fonts.display }}>{brandName}</span> : null}
      {menu.map((m) => (
        <span key={m.id} className={`text-[0.9em] ${m.id === currentId ? "font-semibold text-shade-09" : "text-shade-08/75"}`}>
          {m.label}
          {m.id === currentId ? <span className="mt-[0.2em] block h-[0.15em] rounded-full bg-accent" /> : null}
        </span>
      ))}
    </div>
  );
  const body = (
    <div className="flex flex-col gap-[0.4em] pb-[1.5em]">
      {screen.hero ? <Hero screen={screen} device={device} fonts={fonts} selected={selected === screen.hero.fromRow} onSelect={onSelect} /> : null}
      <div className={`flex flex-col gap-[0.2em] ${tv ? "px-[3%]" : "px-[2%]"} ${screen.hero && tv ? "-mt-[3%] relative" : ""}`}>
        {screen.rows.map((row) => <Row key={row.index} row={row} device={device} fonts={fonts} selected={selected === row.index} onSelect={() => onSelect(row.index)} />)}
        {!screen.rows.length && !screen.hero ? <p className="p-[2em] text-center text-[1em] text-shade-08/75">This screen has no rows yet. Add one in the list.</p> : null}
      </div>
    </div>
  );
  return (
    <div className="flex justify-center">
      {tv ? (
        <div data-theme="dark" className="@container relative aspect-video w-full overflow-hidden rounded-2xl border-4 border-shade-04 bg-shade-02 text-shade-09 shadow-depth" style={{ fontFamily: fonts.body }}>
          <div className="absolute inset-0 overflow-y-auto text-[1.45cqw] scrollbar-none">
            <div className="absolute inset-x-0 top-0 z-1 bg-linear-to-b from-shade-02 to-transparent">{nav}</div>
            {body}
          </div>
        </div>
      ) : (
        <div data-theme="dark" className="@container relative flex aspect-[9/19.5] w-full max-w-[19rem] flex-col overflow-hidden rounded-[2.5rem] border-[6px] border-shade-04 bg-shade-02 text-shade-09 shadow-depth" style={{ fontFamily: fonts.body }}>
          <div className="flex items-center justify-between px-[6%] pt-[1em] pb-[0.5em] text-[4.2cqw]">
            <span className="font-bold" style={{ fontFamily: fonts.display }}>{brandName}</span>
          </div>
          <div className="grow overflow-y-auto text-[4.2cqw] scrollbar-none">{body}</div>
          <div className="text-[3.6cqw]">{nav}</div>
        </div>
      )}
    </div>
  );
}
